if (!customElements.get('featured-worlds-tabs')) {
  customElements.define(
    'featured-worlds-tabs',
    class FeaturedWorldsTabs extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.mobileQuery = window.matchMedia('(max-width: 989px)');
        this.onClick = this.handleClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onBreakpointChange = this.syncViewport.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.onMutations = this.handleMutations.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onBreakpointChange);
        else this.mobileQuery.addListener(this.onBreakpointChange);

        if ('MutationObserver' in window) {
          this.mutationObserver = new MutationObserver(this.onMutations);
          this.mutationObserver.observe(this, { childList: true, subtree: true });
        }

        this.refresh();
        if (!this.tabs.length || !this.panels.length) return;

        this.initialized = true;
        this.classList.add('is-enhanced');
        this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
        this.activate(this.activeIndex);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.mobileQuery?.removeEventListener) this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        else this.mobileQuery?.removeListener(this.onBreakpointChange);
        this.mutationObserver?.disconnect();
        window.cancelAnimationFrame(this.mutationFrame);
        this.initialized = false;
      }

      refresh() {
        this.tabs = [...this.querySelectorAll('[data-featured-world-tab]')];
        this.panels = [...this.querySelectorAll('[data-featured-world-panel]')];
      }

      handleClick(event) {
        const tab = event.target.closest('[data-featured-world-tab]');
        if (!tab || !this.contains(tab)) return;
        this.refresh();
        this.activate(this.tabs.indexOf(tab), { scroll: true });
      }

      handleKeydown(event) {
        const tab = event.target.closest('[data-featured-world-tab]');
        if (!tab || !this.contains(tab)) return;

        this.refresh();
        const currentIndex = this.tabs.indexOf(tab);
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        this.activate(nextIndex, { focus: true, scroll: true });
      }

      activate(index, options = {}) {
        if (index < 0 || index >= this.tabs.length) return;

        this.activeIndex = index;
        const activePanelId = this.tabs[index].getAttribute('aria-controls');
        this.tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const active = panel.id === activePanelId;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', String(this.mobileQuery.matches && !active));
          panel.hidden = this.mobileQuery.matches && !active;
        });

        if (options.focus) this.tabs[index].focus();
        if (options.scroll) this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      syncViewport() {
        this.refresh();
        this.activate(Math.min(this.activeIndex || 0, this.tabs.length - 1));
      }

      handleBlockSelect(event) {
        const panel = event.target?.closest?.('[data-featured-world-panel]');
        if (!panel || !this.contains(panel)) return;

        this.refresh();
        const index = this.panels.indexOf(panel);
        if (index >= 0) this.activate(index, { scroll: this.mobileQuery.matches });
      }

      handleMutations() {
        window.cancelAnimationFrame(this.mutationFrame);
        this.mutationFrame = window.requestAnimationFrame(() => {
          const activePanel = this.panels?.[this.activeIndex];
          const activeBlockId = activePanel?.dataset.blockId;
          this.refresh();
          if (!this.initialized && this.tabs.length && this.panels.length) {
            this.initialized = true;
            this.classList.add('is-enhanced');
          }
          const nextIndex = Math.max(0, this.panels.findIndex((panel) => panel.dataset.blockId === activeBlockId));
          if (this.tabs.length && this.panels.length) this.activate(nextIndex);
        });
      }
    }
  );
}
